import { useEffect, useState } from "react";
import { api, blobProtegido, type Colaborador, type Resumo } from "../api";
import type { Secao } from "../secoes";
import { SECOES } from "../secoes";

/**
 * INÍCIO — o que a pessoa precisa saber assim que entra, e o caminho para o resto.
 *
 * Três blocos, nessa ordem de importância:
 *   1. quem está logado (foto e nome — é o que confirma que a conta é a dela);
 *   2. o que pede ação: folha a conferir, documento vencendo, ponto do dia;
 *   3. os atalhos para todas as seções.
 *
 * Os avisos só aparecem quando existem. Uma tela inicial cheia de "0 pendências"
 * ensina a pessoa a não ler os avisos — e aí ela não lê o que importa.
 */

interface Props {
  colaborador: Colaborador;
  onAbrir: (secao: Secao) => void;
  /** Somente visualização (Portal): a seção está liberada? Ausente = todas. */
  podeAbrir?: (secao: Secao) => boolean;
}

const MESES = [
  "janeiro", "fevereiro", "março", "abril", "maio", "junho",
  "julho", "agosto", "setembro", "outubro", "novembro", "dezembro",
];

const ROTULO_BATIDA: Record<string, string> = {
  entrada: "Entrada",
  saida_almoco: "Saída almoço",
  retorno_almoco: "Retorno",
  saida: "Saída",
};

/** 1.5 → "1h30"; negativo mantém o sinal. */
function horas(v: number): string {
  const sinal = v < 0 ? "-" : "";
  const total = Math.round(Math.abs(v) * 60);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return m ? `${sinal}${h}h${String(m).padStart(2, "0")}` : `${sinal}${h}h`;
}

function saudacao(): string {
  const h = new Date().getHours();
  if (h < 12) return "Bom dia";
  if (h < 18) return "Boa tarde";
  return "Boa noite";
}

function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/);
  return ((partes[0]?.[0] ?? "") + (partes.length > 1 ? partes.at(-1)![0] : "")).toUpperCase();
}

export default function Inicio({ colaborador, onAbrir, podeAbrir = () => true }: Props) {
  const [resumo, setResumo] = useState<Resumo | null>(null);
  const [foto, setFoto] = useState<string | null>(null);

  useEffect(() => {
    api.resumo().then(setResumo).catch(() => setResumo(null));

    // Sem foto no cadastro a rota responde 404 — aí ficam as iniciais.
    let url: string | null = null;
    blobProtegido("/api/minha-foto")
      .then((u) => {
        url = u;
        setFoto(u);
      })
      .catch(() => setFoto(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, []);

  const primeiroNome = colaborador.nome.trim().split(/\s+/)[0];

  return (
    <div className="inicio">
      <header className="perfil">
        {foto ? (
          <img className="perfil-foto" src={foto} alt="" />
        ) : (
          <span className="perfil-foto perfil-iniciais" aria-hidden="true">
            {iniciais(colaborador.nome)}
          </span>
        )}
        <div className="perfil-texto">
          <p className="perfil-saudacao">
            {saudacao()}, {primeiroNome}
          </p>
          <p className="perfil-cargo">
            {[colaborador.cargo, colaborador.departamento].filter(Boolean).join(" · ") || "Colaborador SERCAMP"}
          </p>
        </div>
      </header>

      {resumo && (
        <>
          {/* Só o que pede ação. */}
          {resumo.folhasAConferir.length > 0 && (
            <button className="aviso-acao" onClick={() => onAbrir("folhas")} disabled={!podeAbrir("folhas")}>
              <strong>
                {resumo.folhasAConferir.length === 1
                  ? "1 folha de ponto para conferir"
                  : `${resumo.folhasAConferir.length} folhas de ponto para conferir`}
              </strong>
              <span>
                {resumo.folhasAConferir
                  .slice(0, 3)
                  .map((c) => `${MESES[Number(c.slice(5)) - 1]}/${c.slice(0, 4)}`)
                  .join(", ")}
              </span>
            </button>
          )}
          {resumo.documentosAVencer.length > 0 && (
            <button className="aviso-acao" onClick={() => onAbrir("dados")} disabled={!podeAbrir("dados")}>
              <strong>Documento com vencimento próximo</strong>
              <span>
                {resumo.documentosAVencer
                  .slice(0, 3)
                  .map((d) =>
                    d.dias < 0
                      ? `${d.tipo} (vencido)`
                      : d.dias === 0
                        ? `${d.tipo} (vence hoje)`
                        : `${d.tipo} (${d.dias} dia${d.dias === 1 ? "" : "s"})`,
                  )
                  .join(", ")}
              </span>
            </button>
          )}

          <div className="indicadores">
            <button className="indicador" onClick={() => onAbrir("bater")} disabled={!podeAbrir("bater")}>
              <span>Ponto de hoje</span>
              <strong>
                {resumo.batidasHoje.length === 0
                  ? "Sem marcação"
                  : `${resumo.batidasHoje.length} de 4`}
              </strong>
              {resumo.batidasHoje.length > 0 && (
                <small>
                  {resumo.batidasHoje
                    .map((b) => `${ROTULO_BATIDA[b.tipo] ?? b.tipo} ${b.hora}`)
                    .join(" · ")}
                </small>
              )}
            </button>
            <button className="indicador" onClick={() => onAbrir("banco")} disabled={!podeAbrir("banco")}>
              <span>Banco de horas</span>
              <strong className={resumo.saldoBancoHoras < 0 ? "negativo" : undefined}>
                {horas(resumo.saldoBancoHoras)}
              </strong>
            </button>
            <button className="indicador" onClick={() => onAbrir("extras")} disabled={!podeAbrir("extras")}>
              <span>Horas extras no mês</span>
              <strong>{horas(resumo.horasExtrasMes)}</strong>
            </button>
          </div>
        </>
      )}

      <nav className="atalhos" aria-label="Seções">
        {SECOES.filter((s) => s.id !== "inicio" && podeAbrir(s.id)).map((s) => (
          <button key={s.id} className="atalho" onClick={() => onAbrir(s.id)}>
            <span className="atalho-icone" aria-hidden="true">
              {s.icone}
            </span>
            <span className="atalho-texto">
              <strong>{s.titulo}</strong>
              <small>{s.descricao}</small>
            </span>
          </button>
        ))}
      </nav>
    </div>
  );
}
